import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Router } from "./router";
import { environmentTitlePrefix } from "./lib/env-title";
import "./index.css";
import * as Sentry from "@sentry/react";
import "mapbox-gl/dist/mapbox-gl.css";

// Base title lives in index.html; only the environment tag is added here.
document.title = `${environmentTitlePrefix(import.meta.env)}${document.title}`;

// Init only when SENTRY_DSN is present in the build env. Local dev silent
// unless explicitly opted in. Avoids Sentry's "init with no DSN silently
// discards" footgun where misconfiguration looks like working.
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    // Strip "-v2" suffix so Sentry environments read "production" / "preview"
    // not "production-v2". Railway must set VITE_SENTRY_ENVIRONMENT per env;
    // vite bakes env vars at build time so this resolves at compile, not run.
    environment:
      import.meta.env.VITE_SENTRY_ENVIRONMENT?.replace(/-v2$/, "") ??
      import.meta.env.MODE,
    // Release = git commit SHA. Lets Sentry attribute frontend issues to
    // specific deploys and link errors to the source via vite-plugin-sentry's
    // sourcemap upload. Railway must set VITE_RAILWAY_GIT_COMMIT_SHA per env
    // (mapped from $RAILWAY_GIT_COMMIT_SHA) since vite needs the VITE_ prefix
    // to expose vars to the client bundle.
    release: import.meta.env.VITE_RAILWAY_GIT_COMMIT_SHA,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Tracing — 10% in prod per Sentry's low-traffic guidance (<100K MAU).
    // Admin tool sees ~5K daily req across staff users; 10% gives statistical
    // signal on slow API calls without exhausting the Performance Units quota.
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    // Send distributed tracing headers to ezx-server so server-side spans
    // link to browser-side ones in Sentry's trace view.
    tracePropagationTargets: [
      /^https:\/\/ezx-server-prod\.trypreview\.app\/api/,
      /^https:\/\/ezx-server-preview\.trypreview\.app\/api/,
      "localhost",
    ],
    // Session Replay — 25% session sample per Sentry's docs for low-traffic
    // surfaces (<10K sessions/day): high enough to catch UX bugs, low enough
    // to stay within the Replay quota. Error sessions always 100% — those
    // are the ones we actually need.
    replaysSessionSampleRate: import.meta.env.PROD ? 0.25 : 0,
    replaysOnErrorSampleRate: 1.0,
  });
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Router />
  </StrictMode>
);
